import { Button, Dialog, DialogContent, DialogTitle } from '@commenda/kakashi';
import { classNames } from '@commenda/utils';
import { useState } from 'react';
import { ChangelogEntry } from './types';
import { renderNodes } from './utils';

export function AnnouncementModal({
  entries,
  onClose,
}: {
  /** Unseen entries in oldest-first order; the modal steps through them. */
  entries: ChangelogEntry[];
  /** Called with the entry on screen when the modal is dismissed. */
  onClose: (lastViewed: ChangelogEntry) => void;
}) {
  const [index, setIndex] = useState(0);
  const entry = entries[index];
  const isLastEntry = index === entries.length - 1;

  if (!entry) return null;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose(entry)}>
      <DialogContent
        hideCloseIcon
        aria-describedby={undefined}
        className="flex w-[566px] !max-w-[566px] flex-col gap-0 overflow-hidden rounded-xl p-0"
      >
        {/* Keyed by entry so stepping to the next update resets the scroll. */}
        <div key={entry.id} className="min-h-0 flex-1 overflow-y-auto">
          <div className="flex flex-col gap-6 p-6">
            {entry.imageUrl && (
              <div className="w-full shrink-0 overflow-hidden rounded-lg">
                <img
                  src={entry.imageUrl}
                  alt={entry.title}
                  className="aspect-[2400/1254] w-full object-cover"
                />
              </div>
            )}
            <div className="flex flex-col gap-4">
              <DialogTitle className="!text-2xl !leading-8 font-bold text-text-primary">
                {entry.title}
              </DialogTitle>
              {entry.notes?.content && (
                <div className="text-sm">
                  {renderNodes(entry.notes.content)}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex shrink-0 flex-col gap-3 px-6 pb-6 pt-1.5">
          {entries.length > 1 && (
            <div className="flex items-center justify-center gap-1.5">
              {entries.map((dotEntry, dotIndex) => (
                <button
                  key={dotEntry.id}
                  type="button"
                  aria-label={`Update ${dotIndex + 1} of ${entries.length}`}
                  aria-current={dotIndex === index}
                  onClick={() => setIndex(dotIndex)}
                  className={classNames(
                    'size-1.5 rounded-full transition-colors',
                    dotIndex === index
                      ? 'bg-fg-brand'
                      : 'bg-border-primary hover:bg-fg-quinary'
                  )}
                />
              ))}
            </div>
          )}
          <Button
            size="lg"
            fullWidth
            onClick={() =>
              isLastEntry ? onClose(entry) : setIndex((i) => i + 1)
            }
          >
            {isLastEntry ? 'Got it' : 'Next'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
